import React from 'react';
import styled from 'styled-components';

import { Helmet } from 'react-helmet-async';

import LeftSidebar from './Common/LeftSidebar';

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 45em;
  font-size: 0.85em;
`;

const Content = styled.div`
  text-align: left;
  margin-top: 1em;
`;

const RARPage = () => {
  const pageTitle = "Rounds Above Replacement - Oracle's Elixir";
  const pageDescription =
    "Rounds Above Replacement (RAR), the most advanced player performance system in VALORANT esports.";

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@TimSevenhuysen" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
      </Helmet>
      <LeftSidebar />
      <Wrapper className="mainContent">
        <Content>
          <h2>
            Rounds Above Replacement
          </h2>
          <p>
            <b>What is RAR?</b><br />
            Rounds Above Replacement (RAR) is a player performance metric that captures how many round wins a player contributes to their team, relative to expected output based on agent and map and relative to a replacement-level player. It is similar conceptually to <a href='https://www.mlb.com/glossary/advanced-stats/wins-above-replacement' target="_blank" rel="noopener noreferrer">Wins Above Replacement</a> (WAR) from sports like baseball.
          </p>
          <p>
            RAR was created by <em>Oracle's Elixir</em> founder Tim Sevenhuysen during his time as the Director of Esports Analytics at 100 Thieves, as a tool for talent discovery and roster-building. Now it is available to the entire VALORANT esports community!
          </p>
          <p>
            <b>How is RAR Calculated?</b><br />
            RAR is calculated through changes in round win probability. At any moment of a round, <em>Oracle's Elixir</em>'s proprietary machine learning models accurately predict each team's probability of winning the round. As key actions are taken, players are rewarded for how much they improve their team's probability. In other words, clutching a 1v2 with a Classic against Rifles is worth dramatically more RAR than netting a bunch of easy kills in an anti-eco.
          </p>
          <p>
            <b>Interpreting RAR</b><br />
            The final RAR value you see is based on rounds earned in a complete best-of-three series. A RAR of 2.0 means that the player will earn their team 2 additional rounds across three maps / 72 rounds.
          </p>
          <p>
            A "replacement player" is defined as an average Challengers-level player. Thus, the system is tuned so that an average player in a Challengers league will have a RAR around 0.0.
          </p>
          <p>
            At the VCT level, an average player will have a RAR around 1.5, while the strongest performers will earn RARs of 3.0 or higher.
          </p>
          <p>
            <b>Notes and Cautions</b><br />
            RAR is designed to measure performance across many games/series. RAR values for a single game can drop well into the negatives or ascend into double digits. These are accurate reflections of the player's direct contribution in that game, but over the course of more games the numbers will balance out into a more reasonable range. That's why the <a href='/player-rankings'>Top Players page</a> and player stats for each league/tournament include a "minimum rounds played" filter!
          </p>
          <p>
            <b>FAQs</b><br />
            <em>Why does a player have a RAR of "-"?</em><br />
            In most cases, a blank RAR means that the player's combination of agents and maps has been played too rarely (so far) to generate a baseline to compare their performance against. Once more comparison data has been obtained, or once the player has played more rounds on different agents and maps, they will pass the threshold for reporting and their RAR will be displayed.
          </p>
          <p>
            <b>Still Curious?</b><br />
            If you still have questions about RAR, or other advanced VALORANT metrics, you should jump into the <a href='https://discord.gg/KY6XHaF' target="_blank" rel="noopener noreferrer">OE Discord</a> and join the discussion.
          </p>
        </Content>
      </Wrapper>
    </>
  );
};

export default RARPage;
